import { Tabs } from '@design-systems/canvas-ui'
import React, { useMemo, useCallback } from 'react'
import { useLogClick } from 'utils/analytics'
import { useTranslation } from 'react-i18next'

export interface TimePeriod {
    key: string
    label: string
    index: number
}

interface TimePeriodsProps {
    timePeriods: string[]
    activeTimePeriod: string
    onTabSelect: (timePeriod: string, index: number) => void
    className?: string
    isTimePeriodSticky?: boolean
    page?: string
}

const DEFAULT_ABBREVIATIONS: { [key: string]: string } = {
    // English keys
    today: 'Today',
    yesterday: 'YST',
    'current week': 'WTD',
    week_to_date: 'WTD',
    last_week: 'LW',
    month_to_date: 'MTD',
    year_to_date: 'YTD',
    'next week': 'NW',
    'three weeks out': '3WKS',
    'four weeks out': '4WKS',
    'prior day': 'YST',
    'week to day': 'WTD',
    'last week': 'LW',
    'week to date': 'WTD',
    'month to date': 'MTD',
    'year to date': 'YTD',
    'prior week': 'LW',
    'month to day': 'MTD',
    'year to day': 'YTD',
    'last month': 'LM',
    'prior month': 'LM',
    'last year': 'LY',
    'prior year': 'LY',
    // Spanish translations (mapping to same English abbreviations for display)
    hoy: 'Today',
    ayer: 'YST',
    'semana a la fecha': 'WTD',
    'última semana': 'LW',
    'del mes a la fecha': 'MTD',
    'del año a la fecha': 'YTD',
    'último mes': 'LM',
    'mes pasado': 'LM',
    'último año': 'LY',
    'año pasado': 'LY',
    'próxima semana': 'NW',
}

export const TimePeriods: React.FC<TimePeriodsProps> = ({
    timePeriods,
    activeTimePeriod,
    onTabSelect,
    className = 'time-periods kpi',
    isTimePeriodSticky = true,
    page,
}) => {
    const { i18n } = useTranslation()
    const { logClick } = useLogClick(page || '')
    const getAbbreviatedLabel = useCallback((timePeriod: string): string => {
        // Always return English abbreviations for visual display
        const period = timePeriod.trim().toLowerCase()
        return DEFAULT_ABBREVIATIONS[period] || timePeriod.trim()
    }, [])

    const timePeriodTabs = useMemo(() => {
        return timePeriods.map((timePeriod, index) => ({
            key: timePeriod,
            label: getAbbreviatedLabel(timePeriod),
            fullLabel: timePeriod, // Full translated text for screen readers
            index,
        }))
    }, [timePeriods, getAbbreviatedLabel])

    const handleTabSelect = useCallback(
        (activeTab: string) => {
            const selectedTab = timePeriodTabs.find((t) => t.key === activeTab)
            if (selectedTab) {
                if (page) {
                    logClick(`${page} ${selectedTab.key}`)
                }
                onTabSelect(selectedTab.key, selectedTab.index)
            }
        },
        [timePeriodTabs, onTabSelect, page, logClick],
    )

    const finalClassName = useMemo(() => {
        if (isTimePeriodSticky) {
            return className.replace(/\bkpi\b/g, '').trim()
        }
        if (!className.includes('kpi')) {
            return `${className} kpi`.trim()
        }
        return className
    }, [className, isTimePeriodSticky])

    const stickyWrapperClass = isTimePeriodSticky
        ? 'mpm-time-periods mpm-time-periods--fixed'
        : 'mpm-time-periods mpm-time-periods--inline'

    return (
        <>
            <div className={stickyWrapperClass}>
                <Tabs
                    activeTab={activeTimePeriod}
                    className={finalClassName}
                    disableAdaptive={true}
                    onTabSelect={handleTabSelect}
                >
                    {timePeriodTabs.map((tab) => (
                        <Tabs.Item
                            key={tab.key}
                            name={tab.key}
                            aria-label={tab.fullLabel}
                        >
                            <span lang={i18n.language} className="hc8-sr-only">
                                {tab.fullLabel}
                            </span>
                            <span aria-hidden="true">{tab.label}</span>
                        </Tabs.Item>
                    ))}
                </Tabs>
            </div>
            {isTimePeriodSticky && (
                <div
                    className="sticky-tabs-spacer"
                    style={{ height: '60px' }}
                />
            )}
        </>
    )
}

export type { TimePeriodsProps }
